import {
  APP_INITIALIZER,
  EnvironmentProviders,
  InjectionToken,
  Provider,
  makeEnvironmentProviders,
} from '@angular/core';
import { LokaliseService } from './lokalise.service';
// import { TranslateService } from '@ngx-translate/core';
// import { LokaliseTranslateService } from './lokalise-translate.service';

export enum LokaliseFeatureKind {
  ProjectId,
  EnvironmentEnabled,
}

export interface LokaliseFeature<KindT extends LokaliseFeatureKind> {
  ɵkind: KindT;
  ɵproviders: Provider[];
}

export const LOKALISE_ENV_ENABLED = new InjectionToken<boolean>(
  'LOKALISE_ENV_ENABLED'
);
export const LOKALISE_USER_ENABLED = new InjectionToken<boolean>(
  'LOKALISE_USER_ENABLED'
);
export const LOKALISE_PROJECT_ID = new InjectionToken<string>(
  'LOKALISE_PROJECT_ID'
);

function makeLokaliseFeature<KindT extends LokaliseFeatureKind>(
  kind: KindT,
  providers: Provider[]
): LokaliseFeature<KindT> {
  return {
    ɵkind: kind,
    ɵproviders: providers,
  };
}

export const provideLokalise = (
  ...features: LokaliseFeature<LokaliseFeatureKind>[]
): EnvironmentProviders => {
  const providers: (Provider | EnvironmentProviders)[] = [
    LokaliseService,
    {
      provide: APP_INITIALIZER,
      useFactory: (service: LokaliseService) => () => service.init(),
      deps: [LokaliseService],
      multi: true,
    },
    {
      provide: LOKALISE_USER_ENABLED,
      useValue: !!localStorage.getItem('LOKALISE_ENABLED'),
    },
  ];

  for (const feature of features) {
    providers.push(...feature.ɵproviders);
  }

  return makeEnvironmentProviders(providers);
};

/**
 * Set injection token with project ID
 * @param projectId
 * @returns LokaliseFeature
 *
 * @see {@link provideLokalise}
 */
export const withProjectId = (
  projectId: string
): LokaliseFeature<LokaliseFeatureKind.ProjectId> => {
  return makeLokaliseFeature(LokaliseFeatureKind.ProjectId, [
    {
      provide: LOKALISE_PROJECT_ID,
      useValue: projectId,
    },
  ]);
};

/**
 * Set InjectionToken if this environment has Lokalise script enabled
 * @param enabled
 * @returns LokaliseFeature
 *
 * @see {@link provideLokalise}
 */
export const withEnvironmentEnabled = (
  enabled: boolean
): LokaliseFeature<LokaliseFeatureKind.EnvironmentEnabled> => {
  return makeLokaliseFeature(LokaliseFeatureKind.EnvironmentEnabled, [
    {
      provide: LOKALISE_ENV_ENABLED,
      useValue: enabled,
    },
  ]);
};
